import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {TextArea, TextInput} from '@dropbox/dig-components/text_fields';
import {Text, Title} from '@dropbox/dig-components/typography';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {CareerFrameworkMetadata, CareerFrameworkTree} from 'client';
import {
  CareerFrameworksService,
  Feedback as ApiFeedback,
  Review,
  ReviewGptService,
} from 'client';
import {useGetFeedback} from 'components/manager_feedback/feedback';
import {
  selectedManagerAtom,
  useGetReports,
} from 'components/manager_feedback/feedback_filters';
import {useGetReviews} from 'components/manager_feedback/manager_feedback';
import {
  spritesCycleAtom,
  useGetReviewCycles,
} from 'components/manager_feedback/sprites_cycle_selector';
import {
  findDietManagerReviewForSelectedCycle,
  findReviewForSelectedCycle,
  getSpriteCycleOfFeedback,
  isDietSpritesSelectedCycle,
  isFeedbackInCycleDateRange,
  VIEWABLE_REVIEW_STATUSES,
} from 'components/manager_feedback/utils';
import {sanitizeTextForPDF} from 'helpers/pdf';
import {useAtomValue} from 'jotai';
import {jsPDF} from 'jspdf';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Dropboxer, DropboxerId} from 'store/features/feedback_chat/types';
import {isLocalDevelopment, isSandboxMode} from 'utilities';
import {ANALYTICS_EVENTS, sendAnalyticsEvent} from 'utilities/analytics';

import styles from './review_gpt.module.css';

const FEEDBACK_FORM_LINK = 'https://forms.gle/xHBeejzFXVCSBmQc7';
const SLACK_CHANNEL_LINK =
  'https://dropbox.enterprise.slack.com/archives/C06559B53PX';
const REVIEW_GPT_LINK = 'https://dbx.link/reviewgpt';

// Feature flag to show/hide V1 PDF generation UI
// Set to true to show V1 section, false to hide it
const SHOW_V1_PDF_GENERATION = false;

// Teams to exclude from the CF selector dropdown
const HIDDEN_TEAMS = new Set(['Structured Interviewing']);

const TONE_OPTIONS = [
  {value: '', label: 'Choose One'},
  {value: 'high_performer', label: 'High performer – encourage and develop'},
  {
    value: 'solid_performer',
    label: 'Solid performer – focus on increasing impact',
  },
  {value: 'mixed_performance', label: 'Mixed performance – development needed'},
] as const;

type EmployeeInfo = {
  name: string;
  jobTitle: string;
  jobLevel: string;
  isNamer: boolean;
};

const useGetEmployeeInfo = (
  dropboxerId: DropboxerId
): {
  status: 'loading' | 'error' | 'success';
  employeeInfo: EmployeeInfo | null;
} => {
  const selectedManagerId = useAtomValue(selectedManagerAtom);
  const reportsResult = useGetReports(selectedManagerId);
  const reviewsResult = useGetReviews(dropboxerId);

  // Check loading states
  if (
    reportsResult.status === 'loading' ||
    reviewsResult.status === 'loading'
  ) {
    return {status: 'loading', employeeInfo: null};
  }

  // Try to get info from reports first (has job title and level)
  let dropboxer: Dropboxer | null = null;
  if (reportsResult.status === 'success') {
    dropboxer =
      reportsResult.directReports[dropboxerId] ??
      reportsResult.indirectReports[dropboxerId] ??
      reportsResult.secondaryReports[dropboxerId] ??
      null;
  }

  // Try to get name from reviews if available
  const review =
    reviewsResult.status === 'success' && reviewsResult.reviews?.[0];

  // Build employee info from available data
  if (dropboxer) {
    return {
      status: 'success',
      employeeInfo: {
        name: `${dropboxer.preferred_first_name} ${dropboxer.preferred_last_name}`,
        jobTitle: dropboxer.job_title,
        jobLevel: dropboxer.job_level,
        isNamer: dropboxer.is_namer,
      },
    };
  }

  if (review) {
    return {
      status: 'success',
      employeeInfo: {
        name: `${review.preferred_first_name} ${review.preferred_last_name}`,
        jobTitle: '', // Not available in review data
        jobLevel: '',
        isNamer: false, // Default to false when we only have review data (conservative approach)
      },
    };
  }

  return {status: 'error', employeeInfo: null};
};

type ReviewGPTFormData = {
  impactSummary: string;
  topStrengths: string;
  growthAreas: string;
  careerDevelopment: string;
  toneToUse: string;
};

type ReviewGPTFormProps = {
  employeeInfo: EmployeeInfo;
  review: Review | null;
  managerReview: Review | null;
  isDietCycle: boolean;
  dropboxerId: DropboxerId;
  peerFeedback: GroupedFeedback[];
  managerFeedback: GroupedFeedback[];
  additionalFeedback: AdditionalFeedback[];
  isEmployeeManager: boolean;
};

// Hook to get saved form data from Redis
const useGetReviewGPTFormData = (dropboxerId: DropboxerId) => {
  const isSandbox = isSandboxMode();
  const {data, status, isLoading} = useQuery({
    queryKey: ['/review-gpt/form', dropboxerId, isSandbox],
    queryFn: () =>
      ReviewGptService.getReviewGptFormDataApiV1ReviewGptFormEmployeeIdGet(
        dropboxerId,
        isSandbox
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry on 404 - just show empty form
  });

  return {
    status,
    isLoading,
    formData: data?.form_data ?? null,
  };
};

// Hook to save form data to Redis
const useSaveReviewGPTFormData = (dropboxerId: DropboxerId) => {
  const queryClient = useQueryClient();
  const isSandbox = isSandboxMode();

  return useMutation({
    mutationFn: (formData: ReviewGPTFormData) =>
      ReviewGptService.saveReviewGptFormDataApiV1ReviewGptFormEmployeeIdPut(
        dropboxerId,
        {
          form_data: {
            impact_summary: formData.impactSummary,
            top_strengths: formData.topStrengths,
            growth_areas: formData.growthAreas,
            career_development: formData.careerDevelopment,
            tone_to_use: formData.toneToUse,
          },
        },
        isSandbox
      ),
    onSuccess: () => {
      // Invalidate the query to refetch
      queryClient.invalidateQueries({
        queryKey: ['/review-gpt/form', dropboxerId, isSandbox],
      });
    },
    onError: (error) => {
      console.error('Failed to save ReviewGPT form data:', error);
    },
  });
};

// Hook to fetch career framework for v2 PDF generation
const useGetCareerFramework = (dropboxerId: DropboxerId) => {
  const isSandbox = isSandboxMode();

  return useQuery({
    queryKey: ['/career_framework/by-employee', dropboxerId, isSandbox],
    queryFn: () =>
      CareerFrameworksService.getCareerFrameworkByEmployeeApiV1CareerFrameworkByEmployeeEmployeeIdGet(
        dropboxerId,
        isSandbox
      ),
    staleTime: 1000 * 60 * 60, // 1 hour - career frameworks rarely change
    retry: 1, // Only retry once on failure
  });
};

// Hook to fetch career framework tree structure for the CF selector dropdowns
const useCareerFrameworkTree = () => {
  return useQuery({
    queryKey: ['/api/v1/career_frameworks'],
    queryFn: () =>
      CareerFrameworksService.getCareerFrameworksStructureApiV1CareerFrameworksGet(),
    staleTime: Infinity,
  });
};

// Hook to fetch CF preference from Redis
const useGetCFPreference = (dropboxerId: DropboxerId) => {
  const isSandbox = isSandboxMode();
  return useQuery({
    queryKey: ['/review-gpt/cf-preference', dropboxerId, isSandbox],
    queryFn: () =>
      ReviewGptService.getCfPreferenceApiV1ReviewGptCfPreferenceEmployeeIdGet(
        dropboxerId,
        isSandbox
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
};

// Hook to save CF preference to Redis
const useSaveCFPreference = (dropboxerId: DropboxerId) => {
  const queryClient = useQueryClient();
  const isSandbox = isSandboxMode();

  return useMutation({
    mutationFn: (data: {preferred_team_id: string; preferred_cf_id: string}) =>
      ReviewGptService.saveCfPreferenceApiV1ReviewGptCfPreferenceEmployeeIdPut(
        dropboxerId,
        data,
        isSandbox
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/review-gpt/cf-preference', dropboxerId, isSandbox],
      });
    },
    onError: (error) => {
      console.error('Failed to save CF preference:', error);
    },
  });
};

// Recursively collect all CareerFrameworkMetadata from a tree node
const getAllCareerFrameworks = (
  node: CareerFrameworkTree
): CareerFrameworkMetadata[] => {
  const frameworks = [...node.career_frameworks];
  for (const leaf of node.leafs) {
    frameworks.push(...getAllCareerFrameworks(leaf));
  }
  return frameworks;
};

// Find which top-level team contains a CF matching the given name
const findTeamForCF = (
  tree: CareerFrameworkTree,
  cfName: string
): CareerFrameworkTree | null => {
  for (const team of tree.leafs) {
    const allCFs = getAllCareerFrameworks(team);
    if (allCFs.some((cf) => cf.name === cfName)) {
      return team;
    }
  }
  return null;
};

// Self-review question labels for the PDF table
const SELF_REVIEW_QUESTIONS = [
  {
    label: `Business Impact (What did you achieve?)
Briefly summarize your most important results in 2025 and your impact on the organization and/or team. This can include goals, OKRs (L4+ & managers), KTLO work, innovation programs, and org/team/ERG contributions. Ensure results are supported by measurable/quantitative outcomes.`,
    field: 'question_1' as const,
  },
  {
    label: `Strengths
Identify 1-2 Core Responsibilities that showcase your strengths and provide examples.
* Leverage the Career Framework for your role/level.`,
    field: 'question_2' as const,
  },
  {
    label: `Development Areas
Identify 1-2 Core Responsibilities that are your key development areas. Briefly explain each and outline actions for growth.
* Leverage the Career Framework for your role/level.`,
    field: 'question_3' as const,
  },
  {
    label: `Optional: Career Development
    What's one thing your manager could do to better support your career development goals?`,
    field: 'question_4' as const,
  },
];

const SELF_REVIEW_DIET_QUESTIONS = [
  {
    label: `Strengths
Identify 1-2 Core Responsibilities that showcase your strengths and provide examples.
* Leverage the Career Framework for your role/level.`,
    field: 'question_2' as const,
  },
  {
    label: `Development Areas
Identify 1-2 Core Responsibilities that are your key development areas. Briefly explain each and outline actions for growth.
* Leverage the Career Framework for your role/level.`,
    field: 'question_3' as const,
  },
];

// Diet SPRiTEs review question labels for the PDF table
const DIET_SPRITES_REVIEW_QUESTIONS = [
  {
    label: 'Strengths\nManager assessment of core strengths.',
    field: 'manager_question_2' as const,
  },
  {
    label: 'Development Areas\nManager assessment of development areas.',
    field: 'manager_question_3' as const,
  },
];

// Type for grouped feedback (pairing strengths and development areas)
type GroupedFeedback = {
  feedbackGiverName: string;
  providerTitle: string;
  providerLevel: string;
  strengths: string;
  developmentAreas: string;
};

// Additional feedback from WorkdayEmployeeData.Feedback
type AdditionalFeedback = {
  feedbackGiverName: string;
  providerTitle: string;
  providerLevel: string;
  date: string;
  type: string;
  feedback: string;
};

// Group feedback by giver and pair strengths/development answers
const groupFeedbackByGiver = (
  feedbackList: ApiFeedback[],
  feedbackGivers: Record<string, Dropboxer>,
  source: 'peer' | 'manager'
): GroupedFeedback[] => {
  // For backwards compatibility, treat undefined/null feedback_source as 'peer'
  // Until the migration is run and feedback is refreshed, all feedback will be treated as 'peer'
  const filtered = feedbackList.filter((f) => {
    const feedbackSource = f.feedback_source ?? 'peer';
    return feedbackSource === source;
  });

  // Group by feedback_giver_id only (not date) to ensure we pair all entries from same giver
  const grouped = new Map<string, ApiFeedback[]>();
  filtered.forEach((f) => {
    const key = f.feedback_giver_id;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(f);
  });

  // For each group, collect all feedback content
  // Use the answer field primarily, but if answer is N/A, the content might be in question
  return Array.from(grouped.values()).map((group) => {
    const giver = feedbackGivers[group[0].feedback_giver_id];

    // Collect all strengths and development areas from this giver
    const strengthsAnswers: string[] = [];
    const developmentAnswers: string[] = [];
    const unmatched: string[] = [];

    group.forEach((f) => {
      const questionLower = f.question.toLowerCase();
      // Use answer if available, otherwise skip (question is the prompt, not the response)
      const answer = f.answer;

      // Skip if no actual content
      if (!answer || answer.trim() === '' || answer === 'N/A') return;

      // Try to categorize based on question keywords
      if (
        questionLower.includes('strength') ||
        questionLower.includes('well') ||
        questionLower.includes('positive')
      ) {
        strengthsAnswers.push(answer);
      } else if (
        questionLower.includes('development') ||
        questionLower.includes('growth') ||
        questionLower.includes('improve') ||
        questionLower.includes('area')
      ) {
        developmentAnswers.push(answer);
      } else {
        unmatched.push(answer);
      }
    });

    // If no keyword matches, distribute unmatched answers
    // First half goes to strengths, second half to development
    if (strengthsAnswers.length === 0 && developmentAnswers.length === 0) {
      const half = Math.ceil(unmatched.length / 2);
      strengthsAnswers.push(...unmatched.slice(0, half));
      developmentAnswers.push(...unmatched.slice(half));
    } else {
      // Add any unmatched to whichever is shorter
      unmatched.forEach((answer) => {
        if (strengthsAnswers.length <= developmentAnswers.length) {
          strengthsAnswers.push(answer);
        } else {
          developmentAnswers.push(answer);
        }
      });
    }

    const giverName = giver
      ? `${giver.preferred_first_name} ${giver.preferred_last_name}`
      : 'Unknown';

    // Fall back to dropboxer's job_title/job_level when provider info is N/A or empty
    const providerTitle =
      group[0].provider_title && group[0].provider_title !== 'N/A'
        ? group[0].provider_title
        : giver?.job_title || '';
    const providerLevel =
      group[0].provider_level && group[0].provider_level !== 'N/A'
        ? group[0].provider_level
        : giver?.job_level || '';

    return {
      feedbackGiverName: giverName,
      providerTitle,
      providerLevel,
      strengths:
        strengthsAnswers.length > 0
          ? strengthsAnswers.join('\n\n')
          : '(Not provided)',
      developmentAreas:
        developmentAnswers.length > 0
          ? developmentAnswers.join('\n\n')
          : '(Not provided)',
    };
  });
};

// --- Rich-text HTML-to-PDF types ---

type StyleState = {bold: boolean; italic: boolean; underline: boolean};
type StyledSegment = {text: string} & StyleState;
type HtmlBlock =
  | {kind: 'paragraph'; segments: StyledSegment[]}
  | {kind: 'bullet'; depth: number; segments: StyledSegment[]}
  | {kind: 'numbered'; depth: number; number: number; segments: StyledSegment[]}
  | {kind: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; segments: StyledSegment[]};

type StyledWord = {word: string} & StyleState;
type RenderedWord = {word: string; x: number} & StyleState;
type RenderedLine = RenderedWord[];

// A pre-measured line ready for rendering inside a table cell.
type FlatLine = {
  renderedLine: RenderedLine;
  prefix: string | null; // bullet or "n." marker; non-null only on first line of a block
  indentX: number; // mm from cell-content left edge to first word
};

/**
 * Parse an HTML string into typed HtmlBlock[].
 * Hyperlinks are stripped to plain text; all other formatting is preserved.
 */
const parseHtmlToBlocks = (html: string | null | undefined): HtmlBlock[] => {
  const notProvided: HtmlBlock[] = [
    {
      kind: 'paragraph',
      segments: [
        {text: '(Not provided)', bold: false, italic: false, underline: false},
      ],
    },
  ];
  if (!html || html.trim() === '' || html === 'N/A') return notProvided;

  const domParser = new DOMParser();
  const dom = domParser.parseFromString(html, 'text/html');
  const blocks: HtmlBlock[] = [];

  // Mutable inline segment accumulator; managed via save/restore to avoid
  // mutating function parameters (no-param-reassign).
  let currentSegs: StyledSegment[] = [];

  const flush = (): void => {
    if (currentSegs.length > 0) {
      blocks.push({kind: 'paragraph', segments: currentSegs});
      currentSegs = [];
    }
  };

  type ListContext = {type: 'ul' | 'ol'; depth: number};

  // Per-depth ordered-list counters; updated on a closure variable
  // (not on a function parameter) to comply with no-param-reassign.
  const olCounters: Record<number, number> = {};

  const walk = (
    node: Node,
    style: StyleState,
    listCtx: ListContext | null
  ): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      if (raw) currentSegs.push({text: raw, ...style});
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // Inline formatting
    if (tag === 'b' || tag === 'strong') {
      el.childNodes.forEach((c) => walk(c, {...style, bold: true}, listCtx));
      return;
    }
    if (tag === 'i' || tag === 'em') {
      el.childNodes.forEach((c) => walk(c, {...style, italic: true}, listCtx));
      return;
    }
    if (tag === 'u') {
      el.childNodes.forEach((c) =>
        walk(c, {...style, underline: true}, listCtx)
      );
      return;
    }
    if (tag === 'a') {
      // Strip href; keep inner text as a plain segment
      el.childNodes.forEach((c) => walk(c, style, listCtx));
      return;
    }
    if (tag === 'span' || tag === 'font' || tag === 'mark') {
      el.childNodes.forEach((c) => walk(c, style, listCtx));
      return;
    }

    if (tag === 'br') {
      flush();
      return;
    }
    if (tag === 'p' || tag === 'div') {
      // When inside a <li>, don't flush to the outer blocks array — let the
      // <li> handler collect all segments (Workday stores bullets as <li><p>text</p></li>).
      if (!listCtx) flush();
      el.childNodes.forEach((c) => walk(c, style, listCtx));
      if (!listCtx) flush();
      return;
    }

    // Headings: collect into a temporary accumulator via save/restore
    if (/^h[1-6]$/.test(tag)) {
      flush();
      const level = parseInt(tag[1], 10) as 1 | 2 | 3 | 4 | 5 | 6;
      const saved = currentSegs;
      currentSegs = [];
      el.childNodes.forEach((c) => walk(c, {...style, bold: true}, listCtx));
      const headingSegs = currentSegs;
      currentSegs = saved;
      if (headingSegs.length > 0)
        blocks.push({kind: 'heading', level, segments: headingSegs});
      return;
    }

    // Lists
    if (tag === 'ul' || tag === 'ol') {
      flush();
      const depth = (listCtx?.depth ?? 0) + 1;
      if (tag === 'ol') olCounters[depth] = 0;
      const newCtx: ListContext = {type: tag as 'ul' | 'ol', depth};
      el.childNodes.forEach((c) => walk(c, style, newCtx));
      flush();
      return;
    }
    if (tag === 'li') {
      flush();
      const saved = currentSegs;
      currentSegs = [];
      el.childNodes.forEach((c) => walk(c, style, listCtx));
      const liSegs = currentSegs;
      currentSegs = saved;
      if (liSegs.length > 0) {
        if (listCtx?.type === 'ol') {
          const depth = listCtx.depth;
          olCounters[depth] = (olCounters[depth] ?? 0) + 1;
          blocks.push({
            kind: 'numbered',
            depth,
            number: olCounters[depth],
            segments: liSegs,
          });
        } else {
          blocks.push({
            kind: 'bullet',
            depth: listCtx?.depth ?? 1,
            segments: liSegs,
          });
        }
      }
      return;
    }

    // Default: recurse without style change
    el.childNodes.forEach((c) => walk(c, style, listCtx));
  };

  dom.body.childNodes.forEach((node) =>
    walk(node, {bold: false, italic: false, underline: false}, null)
  );
  flush();

  return blocks.length > 0 ? blocks : notProvided;
};

/** Flatten StyledSegments into individual styled words by splitting on whitespace. */
const segmentsToWords = (segments: StyledSegment[]): StyledWord[] => {
  const words: StyledWord[] = [];
  for (const {text, bold, italic, underline} of segments) {
    const parts = text.split(/\s+/);
    for (const part of parts) {
      if (part) words.push({word: part, bold, italic, underline});
    }
  }
  return words;
};

/**
 * Wrap styled words into rendered lines, computing x offsets for each word.
 * `indentX` shifts all text right of the cell-content left edge (for list items).
 */
const wrapStyledWords = (
  pdfDoc: jsPDF,
  words: StyledWord[],
  maxWidth: number,
  fontSize: number,
  indentX: number
): RenderedLine[] => {
  if (words.length === 0) return [];
  pdfDoc.setFontSize(fontSize);
  pdfDoc.setFont('helvetica', 'normal');
  const spaceWidth = pdfDoc.getTextWidth(' ');
  const availWidth = maxWidth - indentX;

  const lines: RenderedLine[] = [];
  let currentLine: RenderedWord[] = [];
  let currentLineWidth = 0;

  for (const {word, bold, italic, underline} of words) {
    const fontStyle =
      bold && italic
        ? 'bolditalic'
        : bold
        ? 'bold'
        : italic
        ? 'italic'
        : 'normal';
    pdfDoc.setFont('helvetica', fontStyle);
    const wordWidth = pdfDoc.getTextWidth(word);
    const addedWidth =
      currentLine.length > 0 ? spaceWidth + wordWidth : wordWidth;

    if (addedWidth > availWidth && currentLine.length === 0) {
      // Word is wider than available space — force it onto its own line
      lines.push([{word, bold, italic, underline, x: indentX}]);
      currentLineWidth = 0;
    } else if (
      currentLineWidth + addedWidth > availWidth &&
      currentLine.length > 0
    ) {
      lines.push(currentLine);
      currentLine = [{word, bold, italic, underline, x: indentX}];
      currentLineWidth = wordWidth;
    } else {
      const x =
        indentX + currentLineWidth + (currentLine.length > 0 ? spaceWidth : 0);
      currentLine.push({word, bold, italic, underline, x});
      currentLineWidth += addedWidth;
    }
  }

  if (currentLine.length > 0) lines.push(currentLine);
  return lines;
};

/**
 * Measure an HTML string's blocks into a flat FlatLine[] suitable for the
 * chunk-based table row renderers. Applies PDF text sanitization per word.
 */
const measureHtmlToFlatLines = (
  pdfDoc: jsPDF,
  html: string | null | undefined,
  maxWidth: number,
  fontSize: number
): FlatLine[] => {
  const blocks = parseHtmlToBlocks(html);
  const flatLines: FlatLine[] = [];

  for (const block of blocks) {
    let indentX = 0;
    let prefix: string | null = null;
    if (block.kind === 'bullet') {
      indentX = Math.min(block.depth, 3) * 5 + 4;
      prefix = '\u2022'; // •
    } else if (block.kind === 'numbered') {
      indentX = Math.min(block.depth, 3) * 5 + 4;
      prefix = `${block.number}.`;
    }

    // Sanitize and wrap
    const sanitizedSegs = block.segments.map((seg) => ({
      ...seg,
      text: sanitizeTextForPDF(seg.text),
    }));
    const words = segmentsToWords(sanitizedSegs);
    const renderedLines = wrapStyledWords(
      pdfDoc,
      words,
      maxWidth,
      fontSize,
      indentX
    );

    renderedLines.forEach((renderedLine, i) => {
      flatLines.push({
        renderedLine,
        prefix: i === 0 ? prefix : null,
        indentX,
      });
    });
  }

  return flatLines;
};

/**
 * Render one FlatLine at (cellX, y) in the PDF.
 * Switches font per word and draws underlines where needed.
 */
const renderFlatLine = (
  pdfDoc: jsPDF,
  flatLine: FlatLine,
  cellX: number,
  y: number,
  fontSize: number
): void => {
  pdfDoc.setFontSize(fontSize);
  pdfDoc.setFont('helvetica', 'normal');

  if (flatLine.prefix !== null) {
    const prefixWidth = pdfDoc.getTextWidth(flatLine.prefix);
    const prefixX = Math.max(cellX, cellX + flatLine.indentX - prefixWidth - 1);
    pdfDoc.text(flatLine.prefix, prefixX, y);
  }

  for (const {word, bold, italic, underline, x} of flatLine.renderedLine) {
    const fontStyle =
      bold && italic
        ? 'bolditalic'
        : bold
        ? 'bold'
        : italic
        ? 'italic'
        : 'normal';
    pdfDoc.setFont('helvetica', fontStyle);
    pdfDoc.text(word, cellX + x, y);
    if (underline) {
      const w = pdfDoc.getTextWidth(word);
      pdfDoc.line(cellX + x, y + 0.5, cellX + x + w, y + 0.5);
    }
  }
};

// Options for PDF generation to support v1 and v2 variants
type PDFGenerationOptions = {
  includeManagerInput: boolean;
  isDietCycle?: boolean;
  careerFramework?: {content: string; name: string} | null;
  reviewGptLink?: string;
};

const generatePDF = (
  employeeInfo: EmployeeInfo,
  formData: ReviewGPTFormData,
  review: Review | null,
  managerReview: Review | null,
  peerFeedback: GroupedFeedback[],
  managerFeedback: GroupedFeedback[],
  additionalFeedback: AdditionalFeedback[],
  isEmployeeManager: boolean,
  options: PDFGenerationOptions = {includeManagerInput: true}
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25; // Increased margin for better readability
  const contentWidth = pageWidth - margin * 2;
  let yPosition = margin;

  // Helper to check if we need a new page
  const checkNewPage = (requiredSpace: number): void => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Helper function to add wrapped text with proper page break handling
  const addWrappedText = (
    text: string,
    y: number,
    fontSize = 10,
    maxWidth = contentWidth
  ): number => {
    doc.setFontSize(fontSize);
    // Sanitize text before splitting to ensure proper line breaks
    const sanitizedText = sanitizeTextForPDF(text);
    const lines: string[] = doc.splitTextToSize(sanitizedText, maxWidth);
    const lineHeight = fontSize * 0.4;
    let currentY = y;

    for (let i = 0; i < lines.length; i++) {
      // Check if we need a new page before drawing this line
      if (currentY + lineHeight > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      doc.text(lines[i], margin, currentY);
      currentY += lineHeight;
    }

    return currentY + 3;
  };

  // Helper function to add a section header
  const addSectionHeader = (title: string, startY: number): number => {
    checkNewPage(20);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(title, margin, startY);
    return startY + 10;
  };

  // Drop-in replacement for addWrappedText that preserves HTML formatting.
  // Strips hyperlinks but renders bold, italic, underline, lists, and headings.
  const addHtmlContent = (
    html: string | null | undefined,
    startY: number,
    fontSize = 10,
    maxWidth = contentWidth
  ): number => {
    doc.setTextColor(0, 0, 0);

    const blocks = parseHtmlToBlocks(html);
    let currentY = startY;

    for (const block of blocks) {
      let indentX = 0;
      let prefix: string | null = null;
      if (block.kind === 'bullet') {
        indentX = Math.min(block.depth, 3) * 5 + 4;
        prefix = '\u2022';
      } else if (block.kind === 'numbered') {
        indentX = Math.min(block.depth, 3) * 5 + 4;
        prefix = `${block.number}.`;
      }

      // Headings get a larger font size
      const blockFontSize =
        block.kind === 'heading'
          ? Math.max(fontSize, [14, 12, 11, 10, 10, 10][block.level - 1])
          : fontSize;
      const blockLineHeight = blockFontSize * 0.4;

      const sanitizedSegs = block.segments.map((seg) => ({
        ...seg,
        text: sanitizeTextForPDF(seg.text),
      }));
      const words = segmentsToWords(sanitizedSegs);
      const renderedLines = wrapStyledWords(
        doc,
        words,
        maxWidth,
        blockFontSize,
        indentX
      );

      for (let i = 0; i < renderedLines.length; i++) {
        if (currentY + blockLineHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;
        }

        if (i === 0 && prefix !== null) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(blockFontSize);
          const prefixWidth = doc.getTextWidth(prefix);
          const prefixX = Math.max(margin, margin + indentX - prefixWidth - 1);
          doc.text(prefix, prefixX, currentY);
        }

        for (const {word, bold, italic, underline, x} of renderedLines[i]) {
          const fontStyle =
            bold && italic
              ? 'bolditalic'
              : bold
              ? 'bold'
              : italic
              ? 'italic'
              : 'normal';
          doc.setFont('helvetica', fontStyle);
          doc.setFontSize(blockFontSize);
          doc.text(word, margin + x, currentY);
          if (underline) {
            const w = doc.getTextWidth(word);
            doc.line(
              margin + x,
              currentY + 0.5,
              margin + x + w,
              currentY + 0.5
            );
          }
        }
        currentY += blockLineHeight;
      }
      currentY += 2; // small inter-block gap
    }

    return currentY + 1;
  };

  // Helper function to add a subsection with proper page break handling
  const addSubSection = (
    title: string,
    content: string,
    startY: number
  ): number => {
    // Check for space for at least the title + a few lines
    checkNewPage(25);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, startY);
    let currentY = startY + 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const contentOrFallback =
      content && content.trim() ? content : '(Not provided)';
    currentY = addHtmlContent(contentOrFallback, currentY);
    return currentY + 4;
  };

  // Helper to add a table row (2 columns)
  // col1: plain text (question label); col2: HTML content (response)
  // Handles content that may span multiple pages
  // onPageBreak callback receives the margin position and should return the new Y position after drawing headers
  const addTableRow = (
    col1Text: string,
    col2Html: string,
    startY: number,
    drawHeaderAtY?: (y: number) => number
  ): number => {
    const col1Width = contentWidth * 0.4;
    const col2Width = contentWidth * 0.6 - 5;
    const cellPadding = 3;
    const fontSize = 9;
    const lineHeight = 4.5;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');

    // Col1: plain text
    const col1Lines: string[] = doc.splitTextToSize(
      sanitizeTextForPDF(col1Text),
      col1Width - cellPadding * 2
    );

    const col2FlatLines = measureHtmlToFlatLines(
      doc,
      col2Html,
      col2Width - cellPadding * 2,
      fontSize
    );

    const maxLines = Math.max(col1Lines.length, col2FlatLines.length);

    let currentY = startY;
    let lineIndex = 0;

    while (lineIndex < maxLines) {
      const availableHeight = pageHeight - margin - currentY;
      const availableLines = Math.floor(
        (availableHeight - cellPadding * 2) / lineHeight
      );

      if (availableLines < 3) {
        doc.addPage();
        currentY = margin;
        if (drawHeaderAtY) {
          currentY = drawHeaderAtY(margin);
        }
        continue;
      }

      const linesToDraw = Math.min(availableLines, maxLines - lineIndex);
      const chunkHeight = linesToDraw * lineHeight + cellPadding * 2;
      const rowHeight = Math.max(chunkHeight, 15);

      const col1Chunk = col1Lines.slice(lineIndex, lineIndex + linesToDraw);

      // Draw cell borders
      doc.setDrawColor(180, 180, 180);
      doc.rect(margin, currentY, col1Width, rowHeight);
      doc.rect(margin + col1Width, currentY, col2Width, rowHeight);

      doc.setFontSize(fontSize);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      let textY = currentY + cellPadding + 3;

      for (let i = 0; i < linesToDraw; i++) {
        // Col1: plain text
        if (i < col1Chunk.length) {
          doc.setFont('helvetica', 'normal');
          doc.text(col1Chunk[i], margin + cellPadding, textY);
        }
        const col2Idx = lineIndex + i;
        if (col2Idx < col2FlatLines.length) {
          renderFlatLine(
            doc,
            col2FlatLines[col2Idx],
            margin + col1Width + cellPadding,
            textY,
            fontSize
          );
        }
        textY += lineHeight;
      }

      currentY += rowHeight;
      lineIndex += linesToDraw;

      if (lineIndex < maxLines) {
        doc.addPage();
        currentY = margin;
        if (drawHeaderAtY) {
          currentY = drawHeaderAtY(margin);
        }
      }
    }

    return currentY;
  };

  // Helper to add a 3-column table row
  // col1: plain text (giver name); col2/col3: HTML content (strengths / development areas)
  // Handles content that may span multiple pages
  const addThreeColumnTableRow = (
    col1Text: string,
    col2Html: string,
    col3Html: string,
    startY: number
  ): number => {
    const col1Width = contentWidth * 0.2;
    const col2Width = contentWidth * 0.4;
    const col3Width = contentWidth * 0.4 - 5;
    const cellPadding = 3;
    const fontSize = 8;
    const lineHeight = 4.5;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');

    // Col1: plain text
    const col1Lines: string[] = doc.splitTextToSize(
      sanitizeTextForPDF(col1Text),
      col1Width - cellPadding * 2
    );

    const col2FlatLines = measureHtmlToFlatLines(
      doc,
      col2Html,
      col2Width - cellPadding * 2,
      fontSize
    );
    const col3FlatLines = measureHtmlToFlatLines(
      doc,
      col3Html,
      col3Width - cellPadding * 2,
      fontSize
    );

    const maxLines = Math.max(
      col1Lines.length,
      col2FlatLines.length,
      col3FlatLines.length
    );

    let currentY = startY;
    let lineIndex = 0;

    while (lineIndex < maxLines) {
      const availableHeight = pageHeight - margin - currentY;
      const availableLines = Math.floor(
        (availableHeight - cellPadding * 2) / lineHeight
      );

      if (availableLines < 3) {
        doc.addPage();
        currentY = margin;
        continue;
      }

      const linesToDraw = Math.min(availableLines, maxLines - lineIndex);
      const chunkHeight = linesToDraw * lineHeight + cellPadding * 2;
      const rowHeight = Math.max(chunkHeight, 15);

      const col1Chunk = col1Lines.slice(lineIndex, lineIndex + linesToDraw);

      // Draw cell borders
      doc.setDrawColor(180, 180, 180);
      doc.rect(margin, currentY, col1Width, rowHeight);
      doc.rect(margin + col1Width, currentY, col2Width, rowHeight);
      doc.rect(margin + col1Width + col2Width, currentY, col3Width, rowHeight);

      doc.setTextColor(0, 0, 0);
      let textY = currentY + cellPadding + 3;

      for (let i = 0; i < linesToDraw; i++) {
        if (i < col1Chunk.length) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(fontSize);
          doc.text(col1Chunk[i], margin + cellPadding, textY);
        }
        const col2Idx = lineIndex + i;
        if (col2Idx < col2FlatLines.length) {
          renderFlatLine(
            doc,
            col2FlatLines[col2Idx],
            margin + col1Width + cellPadding,
            textY,
            fontSize
          );
        }
        const col3Idx = lineIndex + i;
        if (col3Idx < col3FlatLines.length) {
          renderFlatLine(
            doc,
            col3FlatLines[col3Idx],
            margin + col1Width + col2Width + cellPadding,
            textY,
            fontSize
          );
        }
        textY += lineHeight;
      }

      currentY += rowHeight;
      lineIndex += linesToDraw;

      if (lineIndex < maxLines) {
        doc.addPage();
        currentY = margin;
      }
    }

    return currentY;
  };

  // Helper to add 3-column table header
  const addThreeColumnTableHeader = (
    header1: string,
    header2: string,
    header3: string,
    subHeader2: string,
    subHeader3: string,
    startY: number
  ): number => {
    const col1Width = contentWidth * 0.2;
    const col2Width = contentWidth * 0.4;
    const col3Width = contentWidth * 0.4 - 5;

    // Check if we need a new page - ensure room for header + subheader (~40) + at least one row (30)
    const minRequiredSpace = 40 + 30;
    let currentStartY = startY;
    if (currentStartY + minRequiredSpace > pageHeight - margin) {
      doc.addPage();
      currentStartY = margin;
    }

    // Header row
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, currentStartY, col1Width, 8, 'F');
    doc.rect(margin + col1Width, currentStartY, col2Width, 8, 'F');
    doc.rect(margin + col1Width + col2Width, currentStartY, col3Width, 8, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(header1, margin + 3, currentStartY + 5);
    doc.text(header2, margin + col1Width + 3, currentStartY + 5);
    doc.text(header3, margin + col1Width + col2Width + 3, currentStartY + 5);

    const currentY = currentStartY + 8;

    // Subheader row with descriptions
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setFillColor(250, 250, 250);

    const subHeader2Lines = doc.splitTextToSize(subHeader2, col2Width - 6);
    const subHeader3Lines = doc.splitTextToSize(subHeader3, col3Width - 6);
    const subHeaderHeight =
      Math.max(subHeader2Lines.length, subHeader3Lines.length) * 3 + 6;

    doc.rect(margin, currentY, col1Width, subHeaderHeight, 'F');
    doc.rect(margin + col1Width, currentY, col2Width, subHeaderHeight, 'F');
    doc.rect(
      margin + col1Width + col2Width,
      currentY,
      col3Width,
      subHeaderHeight,
      'F'
    );
    doc.setDrawColor(180, 180, 180);
    doc.rect(margin, currentY, col1Width, subHeaderHeight);
    doc.rect(margin + col1Width, currentY, col2Width, subHeaderHeight);
    doc.rect(
      margin + col1Width + col2Width,
      currentY,
      col3Width,
      subHeaderHeight
    );

    doc.setTextColor(80, 80, 80);
    doc.text(subHeader2Lines, margin + col1Width + 3, currentY + 4);
    doc.text(subHeader3Lines, margin + col1Width + col2Width + 3, currentY + 4);

    return currentY + subHeaderHeight;
  };

  // Helper to add a 4-column table header for Additional Feedback
  const addFourColumnTableHeader = (
    header1: string,
    header2: string,
    header3: string,
    header4: string,
    startY: number
  ): number => {
    const col1Width = contentWidth * 0.2;
    const col2Width = contentWidth * 0.12;
    const col3Width = contentWidth * 0.18;
    const col4Width = contentWidth * 0.5 - 5;

    // Check if we need a new page - ensure room for header (8) + at least one row (30)
    const minRequiredSpace = 8 + 30;
    let currentY = startY;
    if (currentY + minRequiredSpace > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }

    // Header row
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, currentY, col1Width, 8, 'F');
    doc.rect(margin + col1Width, currentY, col2Width, 8, 'F');
    doc.rect(margin + col1Width + col2Width, currentY, col3Width, 8, 'F');
    doc.rect(
      margin + col1Width + col2Width + col3Width,
      currentY,
      col4Width,
      8,
      'F'
    );

    doc.setDrawColor(180, 180, 180);
    doc.rect(margin, currentY, col1Width, 8);
    doc.rect(margin + col1Width, currentY, col2Width, 8);
    doc.rect(margin + col1Width + col2Width, currentY, col3Width, 8);
    doc.rect(
      margin + col1Width + col2Width + col3Width,
      currentY,
      col4Width,
      8
    );

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(header1, margin + 3, currentY + 5);
    doc.text(header2, margin + col1Width + 3, currentY + 5);
    doc.text(header3, margin + col1Width + col2Width + 3, currentY + 5);
    doc.text(
      header4,
      margin + col1Width + col2Width + col3Width + 3,
      currentY + 5
    );

    return currentY + 8;
  };

  // Helper to add a 4-column table row for Additional Feedback
  // col1/col2/col3: plain text (name, type, date); col4: HTML content (feedback)
  // Handles content that may span multiple pages
  const addFourColumnTableRow = (
    col1Text: string,
    col2Text: string,
    col3Text: string,
    col4Html: string,
    startY: number
  ): number => {
    const col1Width = contentWidth * 0.2;
    const col2Width = contentWidth * 0.12;
    const col3Width = contentWidth * 0.18;
    const col4Width = contentWidth * 0.5 - 5;
    const cellPadding = 3;
    const fontSize = 8;
    const lineHeight = 4.5;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'normal');

    // Col1/Col2/Col3: plain text
    const col1Lines: string[] = doc.splitTextToSize(
      sanitizeTextForPDF(col1Text),
      col1Width - cellPadding * 2
    );
    const col2Lines: string[] = doc.splitTextToSize(
      sanitizeTextForPDF(col2Text),
      col2Width - cellPadding * 2
    );
    const col3Lines: string[] = doc.splitTextToSize(
      sanitizeTextForPDF(col3Text),
      col3Width - cellPadding * 2
    );

    const col4FlatLines = measureHtmlToFlatLines(
      doc,
      col4Html,
      col4Width - cellPadding * 2,
      fontSize
    );

    const maxLines = Math.max(
      col1Lines.length,
      col2Lines.length,
      col3Lines.length,
      col4FlatLines.length
    );

    let currentY = startY;
    let lineIndex = 0;

    while (lineIndex < maxLines) {
      const availableHeight = pageHeight - margin - currentY;
      const availableLines = Math.floor(
        (availableHeight - cellPadding * 2) / lineHeight
      );

      if (availableLines < 3) {
        doc.addPage();
        currentY = margin;
        continue;
      }

      const linesToDraw = Math.min(availableLines, maxLines - lineIndex);
      const chunkHeight = linesToDraw * lineHeight + cellPadding * 2;
      const rowHeight = Math.max(chunkHeight, 15);

      const col1Chunk = col1Lines.slice(lineIndex, lineIndex + linesToDraw);
      const col2Chunk = col2Lines.slice(lineIndex, lineIndex + linesToDraw);
      const col3Chunk = col3Lines.slice(lineIndex, lineIndex + linesToDraw);

      // Draw cell borders
      doc.setDrawColor(180, 180, 180);
      doc.rect(margin, currentY, col1Width, rowHeight);
      doc.rect(margin + col1Width, currentY, col2Width, rowHeight);
      doc.rect(margin + col1Width + col2Width, currentY, col3Width, rowHeight);
      doc.rect(
        margin + col1Width + col2Width + col3Width,
        currentY,
        col4Width,
        rowHeight
      );

      doc.setTextColor(0, 0, 0);
      let textY = currentY + cellPadding + 3;

      for (let i = 0; i < linesToDraw; i++) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fontSize);
        if (i < col1Chunk.length) {
          doc.text(col1Chunk[i], margin + cellPadding, textY);
        }
        if (i < col2Chunk.length) {
          doc.text(col2Chunk[i], margin + col1Width + cellPadding, textY);
        }
        if (i < col3Chunk.length) {
          doc.text(
            col3Chunk[i],
            margin + col1Width + col2Width + cellPadding,
            textY
          );
        }
        const col4Idx = lineIndex + i;
        if (col4Idx < col4FlatLines.length) {
          renderFlatLine(
            doc,
            col4FlatLines[col4Idx],
            margin + col1Width + col2Width + col3Width + cellPadding,
            textY,
            fontSize
          );
        }
        textY += lineHeight;
      }

      currentY += rowHeight;
      lineIndex += linesToDraw;

      if (lineIndex < maxLines) {
        doc.addPage();
        currentY = margin;
      }
    }

    return currentY;
  };

  // === TITLE ===
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ReviewGPT Review Document', margin, yPosition);
  yPosition += 12;

  // Instruction with link
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const instructionPrefix = 'Upload your PDF to ';
  const linkText = 'ReviewGPT here';
  const instructionSuffix = ' to create a first draft.';

  // Calculate text widths for positioning
  const prefixWidth = doc.getTextWidth(instructionPrefix);
  const linkWidth = doc.getTextWidth(linkText);

  // Draw prefix text
  doc.text(instructionPrefix, margin, yPosition);

  // Draw link text in blue with underline
  doc.setTextColor(0, 102, 204);
  doc.textWithLink(linkText, margin + prefixWidth, yPosition, {
    url: options.reviewGptLink ?? REVIEW_GPT_LINK,
  });

  // Draw suffix text in black
  doc.setTextColor(0, 0, 0);
  doc.text(instructionSuffix, margin + prefixWidth + linkWidth, yPosition);
  yPosition += 10;

  // Employee Info
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  const employeeLabel = [
    employeeInfo.name,
    employeeInfo.jobTitle,
    employeeInfo.jobLevel,
  ]
    .filter(Boolean)
    .join(' | ');
  doc.text(`Employee: ${employeeLabel}`, margin, yPosition);
  yPosition += 8;

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // === MANAGER INPUT SECTION (v1 only) ===
  if (options.includeManagerInput) {
    yPosition = addSectionHeader('Manager Input', yPosition);

    yPosition = addSubSection(
      'Summary of Their Impact This Year',
      formData.impactSummary,
      yPosition
    );

    yPosition = addSubSection(
      'Top Strengths',
      formData.topStrengths,
      yPosition
    );

    yPosition = addSubSection(
      'Growth or Development Areas',
      formData.growthAreas,
      yPosition
    );

    yPosition = addSubSection(
      'Career Development (Optional)',
      formData.careerDevelopment,
      yPosition
    );

    // Before Tone to Use, check if we can fit it AND the start of Self-Review
    // This prevents "Tone to Use" from being orphaned on its own page
    const toneSpaceNeeded = 30; // title + short content
    const selfReviewStartSpace = 15 + 8 + 40; // header + table header + first row
    const combinedSpace = toneSpaceNeeded + 10 + selfReviewStartSpace; // +10 for spacing

    if (yPosition + combinedSpace > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    // Tone
    const toneLabel =
      TONE_OPTIONS.find((opt) => opt.value === formData.toneToUse)?.label ||
      'Not selected';
    yPosition = addSubSection('Tone to Use', toneLabel, yPosition);

    yPosition += 10;
  }

  // === SELF-REVIEW SECTION ===
  // Safety check in case content above was unexpectedly long
  const minSpaceNeededSelfReview = 15 + 8 + 40;
  if (yPosition + minSpaceNeededSelfReview > pageHeight - margin) {
    doc.addPage();
    yPosition = margin;
  }

  yPosition = addSectionHeader('Self-Review', yPosition);

  // Helper to draw Self-Review table header at a given Y position
  // Returns the new Y position after the header
  const drawSelfReviewTableHeader = (startY: number): number => {
    const col1Width = contentWidth * 0.4;
    const col2Width = contentWidth * 0.6 - 5;
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, startY, col1Width, 8, 'F');
    doc.rect(margin + col1Width, startY, col2Width, 8, 'F');
    doc.setDrawColor(180, 180, 180);
    doc.rect(margin, startY, col1Width, 8);
    doc.rect(margin + col1Width, startY, col2Width, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Question', margin + 3, startY + 5);
    doc.text('Response', margin + col1Width + 3, startY + 5);
    return startY + 8;
  };

  // Draw initial table header
  yPosition = drawSelfReviewTableHeader(yPosition);

  const selfReviewQuestions = options.isDietCycle
    ? SELF_REVIEW_DIET_QUESTIONS
    : SELF_REVIEW_QUESTIONS;

  // Add table rows for self-review, redrawing headers on page breaks
  for (const question of selfReviewQuestions) {
    const response = review ? review[question.field] ?? '' : '(No review data)';
    yPosition = addTableRow(
      question.label,
      response,
      yPosition,
      drawSelfReviewTableHeader
    );
  }

  yPosition += 15;

  // === 360 FEEDBACK SECTION ===
  // Show if there's peer feedback OR if the employee is a manager (to show direct reports section)
  const hasFeedback = peerFeedback.length > 0 || isEmployeeManager;

  if (hasFeedback) {
    // Reserve space for section header (10mm) + subsection header (8mm)
    // + three-column table header area (~50mm) + first table row (~30mm)
    // to prevent the "360 Feedback" heading from being orphaned alone at the bottom of a page.
    const minSpaceFor360Section = 10 + 8 + 50 + 30;
    if (yPosition + minSpaceFor360Section > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
    yPosition = addSectionHeader('360 Feedback', yPosition);
  }

  // === 360 FEEDBACK FROM STAKEHOLDERS ===
  if (peerFeedback.length > 0) {
    // Ensure enough space for subsection header (8) + table header (~50) + first row (~30)
    const minSpaceNeeded = 8 + 50 + 30;
    if (yPosition + minSpaceNeeded > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    // Subsection header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('360 Feedback from Stakeholders', margin, yPosition);
    yPosition += 8;

    yPosition = addThreeColumnTableHeader(
      'Feedback Giver',
      'Strengths',
      'Development Areas',
      'Identify 1-2 Core Responsibilities that are strengths for this Dropboxer, and provide examples. Leverage the Career Framework for their role/level.',
      'Identify 1-2 Core Responsibilities that are development areas for this Dropboxer, and provide examples. Leverage the Career Framework for their role/level.',
      yPosition
    );

    for (const feedback of peerFeedback) {
      // Include title and level in the giver name
      const giverLabel = [
        feedback.feedbackGiverName,
        feedback.providerTitle,
        feedback.providerLevel,
      ]
        .filter(Boolean)
        .join(', ');

      yPosition = addThreeColumnTableRow(
        giverLabel,
        feedback.strengths || '',
        feedback.developmentAreas || '',
        yPosition
      );
    }

    yPosition += 15;
  }

  // === 360 FEEDBACK FROM DIRECT REPORTS (only for managers) ===
  // Show this section for all managers, even if no feedback has been received yet
  if (isEmployeeManager) {
    // Ensure enough space for subsection header (8) + table header (~50) + first row (~30)
    const minSpaceNeededManager = 8 + 50 + 30;
    if (yPosition + minSpaceNeededManager > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    // Subsection header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('360 Feedback from Direct Reports', margin, yPosition);
    yPosition += 8;

    yPosition = addThreeColumnTableHeader(
      'Feedback Giver',
      'Strengths',
      'Development Areas',
      'Which Core Responsibilities do you observe as strengths for your manager? Please support with 1-2 examples each, leveraging the Career Framework for their team/level.',
      'Which Core Responsibilities do you observe as development areas for your manager? Please support with 1-2 examples each, leveraging the Career Framework for their team/level.',
      yPosition
    );

    if (managerFeedback.length > 0) {
      for (const feedback of managerFeedback) {
        // For manager feedback, include title and level in the giver name
        const giverLabel = [
          feedback.feedbackGiverName,
          feedback.providerTitle,
          feedback.providerLevel,
        ]
          .filter(Boolean)
          .join(', ');

        yPosition = addThreeColumnTableRow(
          giverLabel,
          feedback.strengths || '',
          feedback.developmentAreas || '',
          yPosition
        );
      }
    } else {
      // No manager feedback received yet - show empty row with message
      yPosition = addThreeColumnTableRow(
        '—',
        'No feedback from direct reports has been received yet.',
        '—',
        yPosition
      );
    }
  }

  // === ADDITIONAL FEEDBACK SECTION ===
  if (additionalFeedback.length > 0) {
    yPosition += 15;

    // Ensure enough space for section header (15) + table header (8) + first row (~30)
    const minSpaceNeededAdditional = 15 + 8 + 30;
    if (yPosition + minSpaceNeededAdditional > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    yPosition = addSectionHeader('Additional Feedback', yPosition);

    yPosition = addFourColumnTableHeader(
      'Feedback Giver',
      'Given On',
      'Feedback Type',
      'Feedback',
      yPosition
    );

    for (const feedback of additionalFeedback) {
      const giverLabel = [
        feedback.feedbackGiverName,
        feedback.providerTitle,
        feedback.providerLevel,
      ]
        .filter(Boolean)
        .join(', ');

      // Format date compactly (e.g., "2025-06-10T00:00:00" -> "Jun 10, 2025")
      const formattedDate = feedback.date
        ? new Date(feedback.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '';

      yPosition = addFourColumnTableRow(
        giverLabel,
        formattedDate,
        feedback.type || '',
        feedback.feedback || '',
        yPosition
      );
    }
  }

  // === CAREER FRAMEWORK SECTION (v2 only) ===
  if (options.careerFramework) {
    yPosition += 15;

    // Ensure enough space for section header
    const minSpaceNeededCF = 15 + 30;
    if (yPosition + minSpaceNeededCF > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }

    yPosition = addSectionHeader(
      `Core Responsibilities: ${options.careerFramework.name}`,
      yPosition
    );

    // Render career framework content with basic markdown parsing
    const cfLines = options.careerFramework.content.split('\n');

    for (const line of cfLines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        yPosition += 3; // Empty line spacing
        continue;
      }

      // Check for headers
      if (trimmedLine.startsWith('## ')) {
        checkNewPage(15);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        yPosition = addWrappedText(
          trimmedLine.replace(/^##\s*/, ''),
          yPosition,
          12
        );
        yPosition += 2;
      } else if (trimmedLine.startsWith('### ')) {
        checkNewPage(12);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        yPosition = addWrappedText(
          trimmedLine.replace(/^###\s*/, ''),
          yPosition,
          11
        );
      } else if (trimmedLine.startsWith('- ')) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const bulletText = trimmedLine
          .substring(2)
          .replace(/\*\*(.*?)\*\*/g, '$1') // Strip bold **text**
          .replace(/_(.*?)_/g, '$1'); // Strip italic _text_
        yPosition = addWrappedText(
          '• ' + bulletText,
          yPosition,
          10,
          contentWidth - 10
        );
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        // Strip bold and italic markers for plain text rendering
        const plainText = trimmedLine
          .replace(/\*\*(.*?)\*\*/g, '$1') // Strip bold **text**
          .replace(/_(.*?)_/g, '$1'); // Strip italic _text_
        yPosition = addWrappedText(plainText, yPosition);
      }
    }
  }

  // === DIET SPRiTEs REVIEW SECTION (V2 only, non-Diet cycles) ===
  // Diet cycle PDFs omit this section; manager review is shown in Manager Assistant only.
  if (!options.includeManagerInput && managerReview && !options.isDietCycle) {
    // Check if we have enough space for header + table header + at least one row
    // If not, start on a new page to avoid awkward splits
    checkNewPage(80);

    yPosition += 15;

    // Extract year from review cycle for title (e.g., "2025 Diet SPRiTEs Review")
    const reviewYear =
      managerReview.review_cycle.match(/\d{4}/)?.[0] ?? 'Previous';
    yPosition = addSectionHeader(
      `${reviewYear} Diet SPRiTEs Review`,
      yPosition
    );

    // Draw table header for Diet SPRiTEs review
    const drawDietSpritesReviewTableHeader = (startY: number): number => {
      const col1Width = contentWidth * 0.4;
      const col2Width = contentWidth * 0.6 - 5;
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, startY, col1Width, 8, 'F');
      doc.rect(margin + col1Width, startY, col2Width, 8, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.rect(margin, startY, col1Width, 8);
      doc.rect(margin + col1Width, startY, col2Width, 8);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Question', margin + 3, startY + 5);
      doc.text('Response', margin + col1Width + 3, startY + 5);
      return startY + 8;
    };

    yPosition = drawDietSpritesReviewTableHeader(yPosition);

    // Use Diet SPRiTEs review questions (manager_question_2 and manager_question_3)
    for (const question of DIET_SPRITES_REVIEW_QUESTIONS) {
      const response = managerReview[question.field] ?? '';
      yPosition = addTableRow(
        question.label,
        response,
        yPosition,
        drawDietSpritesReviewTableHeader
      );
    }
  }

  // Generate filename with employee name
  const versionSuffix = '';
  const fileName = `ReviewGPT${versionSuffix}_${employeeInfo.name.replace(
    /\s+/g,
    '_'
  )}.pdf`;

  // Download the PDF
  doc.save(fileName);
};

const ReviewGPTForm = ({
  employeeInfo,
  review,
  managerReview,
  isDietCycle,
  dropboxerId,
  peerFeedback,
  managerFeedback,
  additionalFeedback,
  isEmployeeManager,
}: ReviewGPTFormProps) => {
  const [formData, setFormData] = React.useState<ReviewGPTFormData>({
    impactSummary: '',
    topStrengths: '',
    growthAreas: '',
    careerDevelopment: '',
    toneToUse: '',
  });

  // Load saved form data
  const {formData: savedFormData, isLoading: isLoadingSavedData} =
    useGetReviewGPTFormData(dropboxerId);

  // Save form data mutation
  const saveFormDataMutation = useSaveReviewGPTFormData(dropboxerId);

  // Fetch career framework for v2 PDF generation (auto-detected by job title)
  const {
    data: careerFramework,
    isLoading: isLoadingCF,
    isError: isCFError,
  } = useGetCareerFramework(dropboxerId);

  // --- CF selector dropdowns (Redis-backed preference) ---
  const {data: treeData} = useCareerFrameworkTree();
  const {data: cfPreferenceData} = useGetCFPreference(dropboxerId);
  const saveCFPreferenceMutation = useSaveCFPreference(dropboxerId);

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedCFId, setSelectedCFId] = useState<string | null>(null);

  // Fetch CF content by selected ID (for PDF generation)
  const {data: overriddenCF, isLoading: isLoadingOverriddenCF} = useQuery({
    queryKey: ['/career_framework', selectedCFId],
    queryFn: () =>
      CareerFrameworksService.getCareerFrameworkFromIdApiV1CareerFrameworkCareerFrameworkIdGet(
        selectedCFId!
      ),
    enabled: !!selectedCFId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Derive available teams from the tree structure
  const availableTeams = useMemo(
    () =>
      treeData
        ? [...treeData.leafs]
            .filter((t) => !HIDDEN_TEAMS.has(t.name))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [treeData]
  );

  // Derive available CFs from the selected team
  const availableCFs = useMemo(() => {
    const teamNode = availableTeams.find((t) => t.id === selectedTeamId);
    if (!teamNode) return [];
    return getAllCareerFrameworks(teamNode).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [availableTeams, selectedTeamId]);

  // Initialize CF dropdowns from preference saved in Redis, auto-detected CF, or Core Responsibilities fallback
  const cfInitializedRef = useRef(false);
  useEffect(() => {
    if (cfInitializedRef.current || !treeData || availableTeams.length === 0)
      // If CF dropdowns have already been initialized or if there are no available teams, return early
      return;

    // Case 1: saved preference exists — restore it directly
    if (cfPreferenceData?.preference) {
      setSelectedTeamId(cfPreferenceData.preference.preferred_team_id);
      setSelectedCFId(cfPreferenceData.preference.preferred_cf_id);
      cfInitializedRef.current = true;
      return;
    }

    // Wait for both the preference query and the CF query to settle before
    // falling through to the fallback (avoids a flash of Core Responsibilities
    // while the auto-detected CF is still loading).
    const cfQuerySettled = !isLoadingCF; // true once loaded or errored
    if (!cfPreferenceData || !cfQuerySettled) return;

    // Case 2: auto-detected CF loaded — try to match it in the tree
    if (careerFramework) {
      const team = findTeamForCF(treeData, careerFramework.name);
      if (team) {
        const allCFs = getAllCareerFrameworks(team);
        const matchingCF = allCFs.find(
          (cf) => cf.name === careerFramework.name
        );
        setSelectedTeamId(team.id);
        if (matchingCF) {
          setSelectedCFId(matchingCF.cf_id);
          cfInitializedRef.current = true;
          return;
        }
      }
    }

    // Case 3: fallback — auto-detect failed or CF not found in tree.
    // Default to the "Base" team (Core Responsibilities), preferring the CF
    // that matches the employee's job level (e.g. "IC4 Base" for an IC4).
    const baseTeam = availableTeams.find(
      (t) => t.name.toLowerCase() === 'base'
    );
    if (baseTeam) {
      const baseCFs = getAllCareerFrameworks(baseTeam).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const levelMatch = baseCFs.find((cf) =>
        cf.name.toLowerCase().startsWith(employeeInfo.jobLevel.toLowerCase())
      );
      setSelectedTeamId(baseTeam.id);
      setSelectedCFId((levelMatch ?? baseCFs[0])?.cf_id ?? null);
      cfInitializedRef.current = true;
      return;
    }

    // Case 4: last resort — "Base" team not found, select the first available
    // team and CF rather than leaving the dropdowns empty.
    const firstTeam = availableTeams[0];
    const firstCFs = getAllCareerFrameworks(firstTeam).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    setSelectedTeamId(firstTeam.id);
    if (firstCFs.length > 0) {
      setSelectedCFId(firstCFs[0].cf_id);
    }
    cfInitializedRef.current = true;
  }, [
    treeData,
    availableTeams,
    cfPreferenceData,
    careerFramework,
    isLoadingCF,
    isCFError,
    employeeInfo.jobLevel,
  ]);

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);

    // Auto-select the first CF in the newly selected team
    const newTeam = availableTeams.find((t) => t.id === newTeamId);
    if (newTeam) {
      const cfs = getAllCareerFrameworks(newTeam).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      if (cfs.length > 0) {
        setSelectedCFId(cfs[0].cf_id);
        saveCFPreferenceMutation.mutate({
          preferred_team_id: newTeamId,
          preferred_cf_id: cfs[0].cf_id,
        });
      }
    }
  };

  const handleCFChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCFId = e.target.value;
    setSelectedCFId(newCFId);
    if (selectedTeamId) {
      saveCFPreferenceMutation.mutate({
        preferred_team_id: selectedTeamId,
        preferred_cf_id: newCFId,
      });
    }
  };
  // --- end CF selector ---

  // Track which field was last edited and which fields have been saved
  const [lastEditedField, setLastEditedField] = useState<
    keyof ReviewGPTFormData | null
  >(null);
  const [savedFields, setSavedFields] = useState<Set<keyof ReviewGPTFormData>>(
    new Set()
  );

  // Debug panel state (only available in local development)
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const isLocal = isLocalDevelopment();

  // Track if we've initialized from saved data
  const hasInitializedRef = useRef(false);

  // Initialize form with saved data when it loads
  useEffect(() => {
    if (savedFormData && !hasInitializedRef.current) {
      setFormData({
        impactSummary: savedFormData.impact_summary || '',
        topStrengths: savedFormData.top_strengths || '',
        growthAreas: savedFormData.growth_areas || '',
        careerDevelopment: savedFormData.career_development || '',
        toneToUse: savedFormData.tone_to_use || '',
      });
      hasInitializedRef.current = true;
    }
  }, [savedFormData]);

  // Add last edited field to saved fields when save succeeds
  useEffect(() => {
    if (saveFormDataMutation.isSuccess && lastEditedField) {
      setSavedFields((prev) => new Set(prev).add(lastEditedField));
    }
  }, [saveFormDataMutation.isSuccess, lastEditedField]);

  // Debounced save function
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedSave = useCallback(
    (data: ReviewGPTFormData) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveFormDataMutation.mutate(data);
      }, 1000); // Save after 1 second of no changes
    },
    [saveFormDataMutation]
  );

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (field: keyof ReviewGPTFormData, value: string) => {
    const newFormData = {...formData, [field]: value};
    setFormData(newFormData);
    setLastEditedField(field);
    // Auto-save after change
    debouncedSave(newFormData);
  };

  // V1 handler - includes manager input section
  const handleGeneratePDF = () => {
    generatePDF(
      employeeInfo,
      formData,
      review,
      null, // V1 doesn't include previous review
      peerFeedback,
      managerFeedback,
      additionalFeedback,
      isEmployeeManager,
      {includeManagerInput: true}
    );
  };

  // V2 handler - excludes manager input; includes career framework (not prior manager review on Diet cycles)
  const handleGeneratePDFv2 = () => {
    sendAnalyticsEvent(
      ANALYTICS_EVENTS.MANAGER_ASSISTANT.REVIEW_GPT_GENERATE_PDF,
      {
        category: 'Manager Assistant',
      }
    );
    generatePDF(
      employeeInfo,
      formData,
      hasViewableSelfReview ? review : null,
      managerReview, // Include Diet SPRiTEs review in V2 (or null if unavailable)
      peerFeedback,
      managerFeedback,
      additionalFeedback,
      isEmployeeManager,
      {
        includeManagerInput: false,
        isDietCycle,
        careerFramework: overriddenCF ?? careerFramework ?? null,
        reviewGptLink: REVIEW_GPT_LINK,
      }
    );
  };

  const hasViewableSelfReview =
    review !== null && VIEWABLE_REVIEW_STATUSES.includes(review.status);

  // V1 validation - requires manager input fields (careerDevelopment is optional)
  const isFormValidV1 =
    hasViewableSelfReview &&
    formData.impactSummary.trim() !== '' &&
    formData.topStrengths.trim() !== '' &&
    formData.growthAreas.trim() !== '' &&
    formData.toneToUse !== '';

  const isFormValidV2 = !isLoadingCF && !isLoadingOverriddenCF && !isCFError;

  const employeeLabel = [
    employeeInfo.name,
    employeeInfo.jobTitle,
    employeeInfo.jobLevel,
  ]
    .filter(Boolean)
    .join(', ');

  // Helper component to show loading spinner or checkmark next to field labels
  const FieldStatusIndicator = ({field}: {field: keyof ReviewGPTFormData}) => {
    const isLoading =
      saveFormDataMutation.isLoading && lastEditedField === field;
    const isSaved = savedFields.has(field);

    if (isLoading)
      return (
        <span className={styles['field-spinner']}>
          <Spinner size="small" />
        </span>
      );
    if (isSaved) return <span className={styles['field-saved-check']}>✓</span>;
    return null;
  };

  // Show loading state while fetching saved data
  if (isLoadingSavedData) {
    return (
      <div className={styles['loading-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className={styles['review-gpt-container']}>
      {/* V2 Section */}
      <div className={styles['intro-section']}>
        {/* Only show V2 header when V1 is also visible */}
        {SHOW_V1_PDF_GENERATION && (
          <Title size="small" weightVariant="emphasized">
            V2
          </Title>
        )}
        <Text variant="paragraph" tagName="p">
          To streamline the writing process and ensure our reviews reflect
          Dropbox&apos;s tone—clear, fair, and grounded in impact—we&apos;re
          piloting <strong>ReviewGPT</strong>, a manager&apos;s review assistant
          trained to generate professional, high-quality performance reviews
          with minimal lift.
        </Text>
      </div>

      {/* V2 Instructions */}
      <div className={styles['instructions-section']}>
        <Text variant="label" tagName="label" isBold>
          Instructions
        </Text>
        <ol className={styles['instructions-list']}>
          <li>
            <Text variant="paragraph" tagName="span">
              Click <strong>Create ReviewGPT PDF</strong> to create a PDF that
              has all the pertinent info for your direct report: Self-Review,
              Feedback, Career Framework, and last year&apos;s manager review.
            </Text>
          </li>
          <li>
            <Text variant="paragraph" tagName="span">
              Upload your PDF to{' '}
              <a
                href={REVIEW_GPT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['link']}
              >
                ReviewGPT here
              </a>{' '}
              to create a first draft. You can collaborate with it to refine and
              finalize your review.
            </Text>
          </li>
          <li>
            <Text variant="paragraph" tagName="span">
              Please provide{' '}
              <a
                href={FEEDBACK_FORM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['link']}
              >
                pilot feedback here
              </a>{' '}
              to help us make decisions on future rollout to additional
              managers.
            </Text>
          </li>
        </ol>
      </div>

      {/* Career Framework Selection */}
      {treeData && availableTeams.length > 0 && (
        <div className={styles['cf-selector-container']}>
          <div className={styles['cf-selector-field']}>
            <Text variant="label" tagName="label" isBold>
              Team
            </Text>
            <select
              className={styles['select-dropdown']}
              value={selectedTeamId ?? ''}
              onChange={handleTeamChange}
            >
              <option value="" disabled>
                Select a team
              </option>
              {availableTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles['cf-selector-field']}>
            <Text variant="label" tagName="label" isBold>
              Career Framework
            </Text>
            <select
              className={styles['select-dropdown']}
              value={selectedCFId ?? ''}
              onChange={handleCFChange}
              disabled={!selectedTeamId}
            >
              <option value="" disabled>
                Select a career framework
              </option>
              {availableCFs.map((cf) => (
                <option key={cf.cf_id} value={cf.cf_id}>
                  {cf.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* V2 Button */}
      <div className={styles['button-container']}>
        <Button
          variant="primary"
          onClick={handleGeneratePDFv2}
          disabled={!isFormValidV2}
          isLoading={isLoadingCF || isLoadingOverriddenCF}
        >
          Create ReviewGPT PDF
        </Button>
        {isCFError && (
          <Text variant="paragraph" color="faint" size="small">
            Failed to load career framework. No associated career framework
            found.
          </Text>
        )}
      </div>

      {/* Disclaimer for CRs in Footer */}
      <div className={styles['instructions-section']}>
        <Text variant="paragraph" tagName="span">
          <br />
          <i>
            Please ensure the selected Career Frameworks align correctly with
            this worker&apos;s level and role. If the system can&apos;t find a
            match, it will use the generic Core Responsibilities and, in rare
            cases, the CRs may be incorrectly matched.
            <br />
            Reach out to the{' '}
            <strong>
              <a
                href={SLACK_CHANNEL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['link']}
              >
                #sprites-gpt-help
              </a>
            </strong>{' '}
            Slack channel for feedback and&#47;or support.
          </i>
        </Text>
      </div>

      {/* V1 Section - Hidden when SHOW_V1_PDF_GENERATION is false */}
      {SHOW_V1_PDF_GENERATION && (
        <>
          <div className={styles['intro-section']}>
            <Title size="small" weightVariant="emphasized">
              V1
            </Title>
          </div>

          {/* V1 Intro Section */}
          <div className={styles['intro-section']}>
            <Title size="small" weightVariant="emphasized">
              Overview
            </Title>
            <Text variant="paragraph" tagName="p">
              To streamline the writing process and ensure our reviews reflect
              Dropbox&apos;s tone—clear, fair, and grounded in impact—we&apos;re
              piloting Review GPT, a review assistant trained to help managers
              generate professional, high-quality performance reviews with
              minimal lift. Please provide your{' '}
              <a
                href={FEEDBACK_FORM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['link']}
              >
                feedback here
              </a>{' '}
              to help us make decisions on future rollout to additional
              managers.
            </Text>
          </div>

          {/* Instructions Section */}
          <div className={styles['instructions-section']}>
            <Title size="small" weightVariant="emphasized">
              Instructions
            </Title>
            <ol className={styles['instructions-list']}>
              <li>
                <Text variant="paragraph" tagName="span">
                  Fill out the table using short bullet points. Use the
                  Self-Review, Feedback, and GPT Organized Feedback tabs above
                  to help you.
                </Text>
              </li>
              <li>
                <Text variant="paragraph" tagName="span">
                  When finished, click Generate ReviewGPT Doc to create a PDF.
                </Text>
              </li>
              <li>
                <Text variant="paragraph" tagName="span">
                  Upload your PDF to{' '}
                  <a
                    href={REVIEW_GPT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles['link']}
                  >
                    ReviewGPT here
                  </a>{' '}
                  to create a first draft. You can then collaborate with it to
                  refine and finalize your review. (Your text below will be
                  deleted once you close this browser session.)
                </Text>
              </li>
            </ol>
          </div>

          {/* Employee Info */}
          <div className={styles['employee-info']}>
            <Text variant="label" tagName="span" isBold>
              Employee: {employeeLabel}
            </Text>
          </div>

          {/* Form */}
          <div className={styles['form-container']}>
            {/* Summary of Their Impact */}
            <div className={styles['form-field']}>
              <Text variant="label" tagName="label" isBold>
                Summary of Their Impact This Year
                <span className={styles['required-asterisk']}>*</span>
                <FieldStatusIndicator field="impactSummary" />
              </Text>
              <TextArea
                value={formData.impactSummary}
                onChange={(e) =>
                  handleChange('impactSummary', e.currentTarget.value)
                }
                placeholder={`Paste any impact points from their self-review that you strongly agree with.

- Or just write: "Agree with the self-review—please reframe"

- Add 1-2 sentences on any other areas of impact not included in the self review that you'd want to call out`}
                rows={6}
                resizable="auto"
              />
            </div>

            {/* Top Strengths */}
            <div className={styles['form-field']}>
              <Text variant="label" tagName="label" isBold>
                Top Strengths
                <span className={styles['required-asterisk']}>*</span>
                <FieldStatusIndicator field="topStrengths" />
              </Text>
              <TextArea
                value={formData.topStrengths}
                onChange={(e) =>
                  handleChange('topStrengths', e.currentTarget.value)
                }
                placeholder={`2-3 traits or behaviors that elevated their impact, Include examples where possible.

Example: Builds strong cross-functional alignment. Drove complex policy changes across Legal, HR, and Finance with minimal friction.`}
                rows={5}
                resizable="auto"
              />
            </div>

            {/* Growth or Development Areas */}
            <div className={styles['form-field']}>
              <Text variant="label" tagName="label" isBold>
                Growth or Development Areas
                <span className={styles['required-asterisk']}>*</span>
                <FieldStatusIndicator field="growthAreas" />
              </Text>
              <TextArea
                value={formData.growthAreas}
                onChange={(e) =>
                  handleChange('growthAreas', e.currentTarget.value)
                }
                placeholder={`List 2–3 areas where they could focus their efforts to grow. Focus on themes, not isolated incidents, and the impact these have had. Include examples where possible.

Example: Sometimes defaults to precedent when novel strategy would better serve the business.`}
                rows={5}
                resizable="auto"
              />
            </div>

            {/* Career Development (Optional) */}
            <div className={styles['form-field']}>
              <Text variant="label" tagName="label" isBold>
                Career Development (Optional)
                <FieldStatusIndicator field="careerDevelopment" />
              </Text>
              <TextInput
                value={formData.careerDevelopment}
                onChange={(e) =>
                  handleChange('careerDevelopment', e.target.value)
                }
                placeholder="Any additional career guidance to help this Dropboxer grow in the next year and beyond?"
              />
            </div>

            {/* Tone to Use */}
            <div className={styles['form-field']}>
              <Text variant="label" tagName="label" isBold>
                Tone to Use
                <span className={styles['required-asterisk']}>*</span>
                <FieldStatusIndicator field="toneToUse" />
              </Text>
              <select
                value={formData.toneToUse}
                onChange={(e) => handleChange('toneToUse', e.target.value)}
                className={styles['select-dropdown']}
              >
                {TONE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Generate V1 Button */}
            <div className={styles['button-container']}>
              {!hasViewableSelfReview && (
                <Text
                  variant="paragraph"
                  tagName="p"
                  color="subtle"
                  className={styles['pdf-disabled-message']}
                >
                  PDF generation is disabled because the self-review has not
                  been submitted yet.
                </Text>
              )}
              {/* V1 Button - requires manager input, includes Manager Input section in PDF */}
              <Button
                variant="primary"
                onClick={handleGeneratePDF}
                disabled={!isFormValidV1}
              >
                Generate ReviewGPT Doc
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Debug Panel - Only visible in local development */}
      {isLocal && (
        <div className={styles['debug-section']}>
          <Button
            variant="transparent"
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            size="small"
          >
            {showDebugPanel ? 'Hide' : 'Show'} Text Sanitization Debug Panel
          </Button>

          {showDebugPanel && (
            <div
              style={{
                marginTop: '16px',
                padding: '16px',
                background: '#1a1a1a',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: '#e0e0e0',
                maxHeight: '600px',
                overflow: 'auto',
              }}
            >
              <h3 style={{margin: '0 0 16px 0', color: '#fff'}}>
                Text Sanitization Debug Panel
              </h3>

              {/* Self Review Section */}
              <details open>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#4fc3f7',
                  }}
                >
                  Self Review
                </summary>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '15%',
                        }}
                      >
                        Field
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '42%',
                        }}
                      >
                        Raw
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '43%',
                        }}
                      >
                        After sanitizeTextForPDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {field: 'question_1', label: 'Business Impact'},
                      {field: 'question_2', label: 'Strengths'},
                      {field: 'question_3', label: 'Development Areas'},
                      {field: 'question_4', label: 'Career Development'},
                    ].map(({field, label}) => {
                      const raw =
                        review?.[field as keyof Review]?.toString() ?? '';
                      const afterSanitize = sanitizeTextForPDF(raw);
                      return (
                        <tr key={field}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {raw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {afterSanitize || '(empty)'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>

              {/* Manager Inputs Section */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#4fc3f7',
                  }}
                >
                  Manager Inputs
                </summary>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '15%',
                        }}
                      >
                        Field
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '42%',
                        }}
                      >
                        Raw
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '43%',
                        }}
                      >
                        After sanitizeTextForPDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {field: 'impactSummary', label: 'Impact Summary'},
                      {field: 'topStrengths', label: 'Top Strengths'},
                      {field: 'growthAreas', label: 'Growth Areas'},
                      {field: 'careerDevelopment', label: 'Career Development'},
                      {field: 'toneToUse', label: 'Tone'},
                    ].map(({field, label}) => {
                      const raw =
                        formData[field as keyof ReviewGPTFormData] ?? '';
                      const afterSanitize = sanitizeTextForPDF(raw);
                      return (
                        <tr key={field}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {raw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {afterSanitize || '(empty)'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>

              {/* Peer Feedback Section */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#4fc3f7',
                  }}
                >
                  Peer Feedback ({peerFeedback.length} entries)
                </summary>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '15%',
                        }}
                      >
                        Provider / Field
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '42%',
                        }}
                      >
                        Raw
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '43%',
                        }}
                      >
                        After sanitizeTextForPDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {peerFeedback.flatMap((feedback, idx) => {
                      const strengthsRaw = feedback.strengths ?? '';
                      const strengthsAfterSanitize =
                        sanitizeTextForPDF(strengthsRaw);
                      const devRaw = feedback.developmentAreas ?? '';
                      const devAfterSanitize = sanitizeTextForPDF(devRaw);
                      return [
                        <tr key={`${idx}-strengths`}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            {feedback.feedbackGiverName} - Strengths
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {strengthsRaw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {strengthsAfterSanitize || '(empty)'}
                          </td>
                        </tr>,
                        <tr key={`${idx}-dev`}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            {feedback.feedbackGiverName} - Dev Areas
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {devRaw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {devAfterSanitize || '(empty)'}
                          </td>
                        </tr>,
                      ];
                    })}
                  </tbody>
                </table>
              </details>

              {/* Previous Review Section */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#4fc3f7',
                  }}
                >
                  Previous Review
                </summary>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '15%',
                        }}
                      >
                        Field
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '42%',
                        }}
                      >
                        Raw
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '43%',
                        }}
                      >
                        After sanitizeTextForPDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {field: 'manager_question_1', label: 'Business Impact'},
                      {field: 'manager_question_2', label: 'Strengths'},
                      {field: 'manager_question_3', label: 'Development Areas'},
                      {
                        field: 'manager_question_4',
                        label: 'Career Development',
                      },
                    ].map(({field, label}) => {
                      const raw =
                        managerReview?.[field as keyof Review]?.toString() ??
                        '';
                      const afterSanitize = sanitizeTextForPDF(raw);
                      return (
                        <tr key={field}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {raw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {afterSanitize || '(empty)'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>

              {/* Additional Feedback Section (includes Spotlight Awards) */}
              <details>
                <summary
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#4fc3f7',
                  }}
                >
                  Additional Feedback / Spotlight Awards (
                  {additionalFeedback.length} entries)
                </summary>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '16px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '15%',
                        }}
                      >
                        Type / Provider
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '42%',
                        }}
                      >
                        Raw
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #444',
                          width: '43%',
                        }}
                      >
                        After sanitizeTextForPDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalFeedback.map((feedback, idx) => {
                      const raw = feedback.feedback ?? '';
                      const afterSanitize = sanitizeTextForPDF(raw);
                      return (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                            }}
                          >
                            <strong>{feedback.type}</strong>
                            <br />
                            {feedback.feedbackGiverName}
                            <br />
                            <span style={{color: '#888', fontSize: '10px'}}>
                              {feedback.date}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {raw || '(empty)'}
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #333',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: '200px',
                            }}
                          >
                            {afterSanitize || '(empty)'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type ReviewGPTControllerProps = {
  dropboxerId: DropboxerId;
};

export const ReviewGPTController = ({
  dropboxerId,
}: ReviewGPTControllerProps) => {
  const {status, employeeInfo} = useGetEmployeeInfo(dropboxerId);
  const reviewsResult = useGetReviews(dropboxerId);
  const feedbackResult = useGetFeedback(dropboxerId);
  const selectedCycle = useAtomValue(spritesCycleAtom);
  const isSandbox = isSandboxMode();
  const {reviewCycles} = useGetReviewCycles();

  // Get reports for this employee to check if they're a manager
  const employeeReportsResult = useGetReports(dropboxerId);

  if (
    status === 'loading' ||
    reviewsResult.status === 'loading' ||
    feedbackResult.status === 'loading' ||
    employeeReportsResult.status === 'loading'
  ) {
    return (
      <div className={styles['loading-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!employeeInfo) {
    return (
      <div className={styles['error-container']}>
        <Text variant="paragraph" tagName="p" size="xlarge">
          Could not load employee information.
        </Text>
      </div>
    );
  }

  const isDietCycle = isDietSpritesSelectedCycle(selectedCycle);

  const review =
    reviewsResult.status === 'success' && reviewsResult.reviews
      ? findReviewForSelectedCycle(
          reviewsResult.reviews,
          selectedCycle,
          isSandbox
        ) ?? null
      : null;

  const managerReview =
    reviewsResult.status === 'success' && reviewsResult.reviews
      ? findDietManagerReviewForSelectedCycle(
          reviewsResult.reviews,
          selectedCycle,
          isSandbox
        ) ?? null
      : null;

  // Build feedback givers map from feedback response
  const feedbackGivers: Record<string, Dropboxer> = {};
  if (
    feedbackResult.status === 'success' &&
    feedbackResult.feedbackGiversState
  ) {
    feedbackResult.feedbackGiversState.allFeedbackGiverIds.forEach((id) => {
      const giver = feedbackResult.feedbackGiversState?.byFeedbackGiverId[id];
      if (giver) {
        feedbackGivers[id] = giver;
      }
    });
  }

  // Get ALL feedback from API response
  const targetCycle =
    selectedCycle ?? (isSandbox ? '2025 SPRiTEs' : '2025 SPRiTEs Feedback');
  const allFeedbackList: ApiFeedback[] = [];
  if (feedbackResult.status === 'success' && feedbackResult.feedbackState) {
    feedbackResult.feedbackState.allFeedbackIds.forEach((feedbackId) => {
      const fb = feedbackResult.feedbackState?.byFeedbackId[feedbackId];
      if (fb) {
        allFeedbackList.push({
          id: feedbackId,
          employee_id: dropboxerId,
          feedback_giver_id: fb.feedbackGiverId,
          question: fb.question,
          answer: fb.htmlAnswer,
          date: fb.date,
          type: fb.type,
          provider_title: fb.providerTitle,
          provider_level: fb.providerLevel,
          feedback_source: fb.feedbackSource,
        });
      }
    });
  }

  const isCurrentCycleFeedback = (fb: ApiFeedback): boolean => {
    if (reviewCycles && reviewCycles.length > 0 && targetCycle) {
      return isFeedbackInCycleDateRange(fb.date, targetCycle, reviewCycles);
    }
    return getSpriteCycleOfFeedback(fb.type, fb.date) === targetCycle;
  };

  // Helper to check if feedback is "standard" SPRiTEs feedback (not spotlight, anytime, etc.)
  const isStandardSpritesFeedback = (fb: ApiFeedback): boolean => {
    const typeLower = fb.type.toLowerCase();
    // Exclude spotlight awards, anytime feedback, self-requested, and other special types
    // Spotlight awards: "Spotlight Award - <date>"
    // Anytime feedback: "Anytime Feedback - <date>"
    // Self-requested: contains "Self Requested"
    const isSpotlight = typeLower.includes('spotlight award');
    const isAnytime = typeLower.includes('anytime feedback');
    const isSelfRequested = typeLower.includes('self requested');
    return !isSpotlight && !isAnytime && !isSelfRequested;
  };

  // Filter for 2025 SPRiTEs peer feedback only (for 360 Stakeholders table)
  const currentCyclePeerFeedback = allFeedbackList.filter((fb) => {
    const feedbackSource = fb.feedback_source ?? 'peer';
    return (
      isCurrentCycleFeedback(fb) &&
      isStandardSpritesFeedback(fb) &&
      feedbackSource === 'peer'
    );
  });

  // Filter for 2025 manager feedback only (for 360 Direct Reports table)
  const currentCycleManagerFeedback = allFeedbackList.filter((fb) => {
    const feedbackSource = fb.feedback_source ?? 'peer';
    return (
      isCurrentCycleFeedback(fb) &&
      isStandardSpritesFeedback(fb) &&
      feedbackSource === 'manager'
    );
  });

  // Group feedback for 360 tables
  const peerFeedback = groupFeedbackByGiver(
    currentCyclePeerFeedback,
    feedbackGivers,
    'peer'
  );
  const managerFeedback = groupFeedbackByGiver(
    currentCycleManagerFeedback,
    feedbackGivers,
    'manager'
  );

  // Additional Feedback = Current cycle feedback NOT in the 360 tables
  // (spotlight awards, anytime feedback, self-requested from 2025)
  const additionalFeedbackIds = new Set([
    ...currentCyclePeerFeedback.map((fb) => fb.id),
    ...currentCycleManagerFeedback.map((fb) => fb.id),
  ]);

  const additionalFeedback: AdditionalFeedback[] = allFeedbackList
    .filter(
      (fb) => !additionalFeedbackIds.has(fb.id) && isCurrentCycleFeedback(fb)
    )
    .map((fb) => {
      const giver = feedbackGivers[fb.feedback_giver_id];
      const giverName = giver
        ? `${giver.preferred_first_name} ${giver.preferred_last_name}`
        : 'Unknown';

      // Fall back to dropboxer's job_title/job_level when provider info is N/A or empty
      const providerTitle =
        fb.provider_title && fb.provider_title !== 'N/A'
          ? fb.provider_title
          : giver?.job_title || '';
      const providerLevel =
        fb.provider_level && fb.provider_level !== 'N/A'
          ? fb.provider_level
          : giver?.job_level || '';

      return {
        feedbackGiverName: giverName,
        providerTitle,
        providerLevel,
        date: fb.date,
        type: fb.type,
        feedback: fb.answer ?? '',
      };
    });

  // Check if the employee is a manager (has direct reports)
  const isEmployeeManager =
    employeeReportsResult.status === 'success' &&
    Object.keys(employeeReportsResult.directReports ?? {}).length > 0;

  return (
    <ReviewGPTForm
      key={dropboxerId} // Force remount when employee changes to reset form state
      employeeInfo={employeeInfo}
      review={review}
      managerReview={managerReview}
      isDietCycle={isDietCycle}
      dropboxerId={dropboxerId}
      peerFeedback={peerFeedback}
      managerFeedback={managerFeedback}
      additionalFeedback={additionalFeedback}
      isEmployeeManager={isEmployeeManager}
    />
  );
};
